import React, { useState, useEffect, useRef } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { fuzzyMatch } from "../lib/fuzzySearch";
import { HelpBubble } from "../components/HelpBubble";
import {
  Plus,
  Edit,
  Trash2,
  FileText,
  Search,
  Loader2,
  Upload,
  ChevronDown,
  ChevronUp,
  Eye,
  Download,
  X,
  Lock,
  Unlock,
  ShieldAlert,
  Copy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  templateCategories,
  addendumCategories,
  sectionTypes,
  addendumSectionTypes,
  governingLawOptions,
  professionalServicesPreset,
  addendumPreset,
  extensionPreset,
  buildSectionsFromPreset,
} from "../components/templates/templateData";

function SectionCard({ section, index, total, onUpdate, onRemove, onMove, activeSectionTypes }) {
  const sectionDef = activeSectionTypes.find((t) => t.value === section.type);
  const isLocked = !section.is_editable;

  return (
    <Card className={`border ${isLocked ? "border-amber-200 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-950/10" : ""}`}>
      <CardContent className="pt-4">
        <div className="flex items-start gap-3">
          <div className="flex flex-col gap-1 pt-2">
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove(index, "up")} disabled={index === 0}>
              <ChevronUp size={14} />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove(index, "down")} disabled={index === total - 1}>
              <ChevronDown size={14} />
            </Button>
          </div>

          <div className="flex-1 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Section Type</Label>
                <Select value={section.type} onValueChange={(value) => onUpdate(index, "type", value)}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {activeSectionTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Section Name</Label>
                <Input
                  value={section.name}
                  onChange={(e) => onUpdate(index, "name", e.target.value)}
                  placeholder="Section name"
                  className="rounded-sm h-9"
                />
              </div>
              <div className="flex items-end gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-sm">
                  <Switch
                    checked={section.is_editable}
                    onCheckedChange={(checked) => onUpdate(index, "is_editable", checked)}
                  />
                  <span className="text-xs text-slate-500">{section.is_editable ? "Editable" : "Locked"}</span>
                </div>
              </div>
              <div className="flex items-end gap-3">
                {isLocked && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-sm">
                    <Switch
                      checked={section.allow_override}
                      onCheckedChange={(checked) => onUpdate(index, "allow_override", checked)}
                    />
                    <span className="text-xs text-slate-500">Allow Override</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Content</Label>
              <Textarea
                value={section.content}
                onChange={(e) => onUpdate(index, "content", e.target.value)}
                placeholder={sectionDef?.description || `Enter ${section.name || "section"} content...`}
                rows={5}
                className="rounded-sm text-sm"
              />
            </div>

            {isLocked && !section.allow_override && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <Lock size={12} />
                This section will be non-editable when drafting contracts
              </p>
            )}
            {isLocked && section.allow_override && (
              <p className="text-xs text-blue-600 flex items-center gap-1">
                <ShieldAlert size={12} />
                Locked by default, but can be overridden in exceptional circumstances during drafting
              </p>
            )}
          </div>

          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => onRemove(index)}>
            <X size={16} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TemplatesPage() {
  const { user, canDo } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [templateTypeTab, setTemplateTypeTab] = useState("contract");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [createMode, setCreateMode] = useState("builder");

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    content: "",
    clauses: [],
    sections: [],
    file_url: null,
    file_name: null,
    governing_law: "",
    template_metadata: {
      company_name: "",
      company_address: "",
      termination_notice_days: "30",
      currency: "GBP",
    },
  });

  const canManage = canDo("templates.create_edit");

  useEffect(() => { fetchTemplates(); }, [categoryFilter, templateTypeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      params.append("template_type", templateTypeTab);
      const response = await apiClient.get(`/templates?${params.toString()}`);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const response = await apiClient.post("/templates/upload", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData((prev) => ({ ...prev, file_url: response.data.file_id, file_name: response.data.filename }));
      toast.success("File uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const activeSectionTypes = templateTypeTab === "addendum" ? addendumSectionTypes : sectionTypes;
  const activeCategories = templateTypeTab === "addendum" ? addendumCategories : templateCategories;

  const handleAddSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      name: "",
      type: "custom",
      content: "",
      is_editable: true,
      allow_override: false,
      order: formData.sections.length,
    };
    setFormData((prev) => ({ ...prev, sections: [...prev.sections, newSection] }));
  };

  const handleAddQuickSection = (typeValue) => {
    const sectionDef = activeSectionTypes.find((t) => t.value === typeValue);
    const newSection = {
      id: `section-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      name: sectionDef?.label || "",
      type: typeValue,
      content: "",
      is_editable: sectionDef?.editable ?? true,
      allow_override: false,
      order: formData.sections.length,
    };
    setFormData((prev) => ({ ...prev, sections: [...prev.sections, newSection] }));
  };

  const handleLoadProfessionalServicesPreset = () => {
    setFormData((prev) => ({ ...prev, sections: buildSectionsFromPreset(professionalServicesPreset) }));
    toast.success("Professional Services template structure loaded");
  };

  const handleLoadAddendumPreset = () => {
    setFormData((prev) => ({ ...prev, sections: buildSectionsFromPreset(addendumPreset) }));
    toast.success("Agreement Addendum template structure loaded");
  };

  const handleLoadExtensionPreset = () => {
    setFormData((prev) => ({ ...prev, sections: buildSectionsFromPreset(extensionPreset) }));
    toast.success("Extension Addendum template structure loaded");
  };

  const handleUpdateSection = (index, field, value) => {
    setFormData((prev) => {
      const newSections = [...prev.sections];
      newSections[index] = { ...newSections[index], [field]: value };

      if (field === "type") {
        const sectionDef = activeSectionTypes.find((t) => t.value === value);
        newSections[index].is_editable = sectionDef?.editable ?? true;
        newSections[index].allow_override = false;
        if (!newSections[index].name) {
          newSections[index].name = sectionDef?.label || "";
        }
      }

      return { ...prev, sections: newSections };
    });
  };

  const handleRemoveSection = (index) => {
    setFormData((prev) => ({ ...prev, sections: prev.sections.filter((_, i) => i !== index) }));
  };

  const handleMoveSection = (index, direction) => {
    setFormData((prev) => {
      const newSections = [...prev.sections];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= newSections.length) return prev;
      [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
      newSections.forEach((s, i) => (s.order = i));
      return { ...prev, sections: newSections };
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category) {
      toast.error("Please fill in template name and category");
      return;
    }
    if (createMode === "builder" && formData.sections.length === 0) {
      toast.error("Please add at least one section to the template");
      return;
    }

    setSaving(true);
    try {
      let content = formData.content;
      if (createMode === "builder" && formData.sections.length > 0) {
        content = formData.sections.map((s) => `## ${s.name}\n\n${s.content}`).join("\n\n---\n\n");
      }

      const payload = {
        ...formData,
        content,
        template_type: templateTypeTab,
        sections: formData.sections.map((s) => ({
          ...s,
          allow_override: s.allow_override || false,
        })),
      };

      if (selectedTemplate) {
        await apiClient.put(`/templates/${selectedTemplate.id}`, payload);
        toast.success("Template updated successfully");
      } else {
        await apiClient.post("/templates", payload);
        toast.success("Template created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await apiClient.delete(`/templates/${selectedTemplate.id}`);
      toast.success("Template deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete template");
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    if (template.template_type === "addendum" && templateTypeTab !== "addendum") {
      setTemplateTypeTab("addendum");
    }
    setFormData({
      name: template.name,
      category: template.category,
      description: template.description || "",
      content: template.content,
      clauses: template.clauses || [],
      sections: (template.sections || []).map((s) => ({ ...s, allow_override: s.allow_override || false })),
      file_url: template.file_url || null,
      file_name: template.file_name || null,
      governing_law: template.governing_law || "",
      template_metadata: template.template_metadata || {
        company_name: "",
        company_address: "",
        termination_notice_days: "30",
        currency: "GBP",
      },
    });
    setCreateMode(template.sections?.length > 0 ? "builder" : "upload");
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedTemplate(null);
    setFormData({
      name: "", category: "", description: "", content: "", clauses: [], sections: [],
      file_url: null, file_name: null, governing_law: "",
      template_metadata: { company_name: "", company_address: "", termination_notice_days: "30", currency: "GBP" },
    });
    setCreateMode("builder");
  };

  const handleClone = async (template) => {
    try {
      await apiClient.post(`/templates/${template.id}/clone`);
      toast.success(`Template "${template.name}" cloned successfully`);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clone template");
    }
  };

  const filteredTemplates = templates.filter((template) => {
    if (!searchQuery) return true;
    return fuzzyMatch(searchQuery, [template.name, template.category, template.description]);
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Templates <HelpBubble tip="Standard contract templates used across the organisation. Generate new templates with AI or create manually. Templates feed into the Contract Creation Wizard." />
          </h1>
          <p className="text-slate-500 mt-1">Create and manage contract and addendum templates</p>
        </div>
        {canManage && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2 rounded-sm bg-slate-900 hover:bg-slate-800 dark:bg-cyan-500 dark:text-slate-900" data-testid="create-template-btn">
            <Plus size={18} />
            New {templateTypeTab === "addendum" ? "Addendum" : "Contract"} Template
          </Button>
        )}
      </div>

      {/* Template Type Tabs */}
      <Tabs value={templateTypeTab} onValueChange={(v) => { setTemplateTypeTab(v); setCategoryFilter("all"); }} className="w-full">
        <TabsList>
          <TabsTrigger value="contract" className="gap-2" data-testid="contract-templates-tab"><FileText size={14} />Contract Templates</TabsTrigger>
          <TabsTrigger value="addendum" className="gap-2" data-testid="addendum-templates-tab"><FileText size={14} />Addendum Templates</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search & Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input placeholder="Search templates..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-sm" data-testid="search-templates-input" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {activeCategories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>
      ) : filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <FileText size={18} className="text-slate-500" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <Badge variant="outline" className="mt-1 text-xs">{template.category}</Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 line-clamp-2 mb-3">{template.description || "No description"}</p>

                {template.sections?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {template.sections.slice(0, 3).map((section, idx) => (
                      <Badge key={section.name || section.type || idx} variant="secondary" className="text-xs">
                        {section.is_editable ? <Unlock size={10} className="mr-1" /> : <Lock size={10} className="mr-1" />}
                        {section.name || section.type}
                      </Badge>
                    ))}
                    {template.sections.length > 3 && (
                      <Badge variant="secondary" className="text-xs">+{template.sections.length - 3} more</Badge>
                    )}
                  </div>
                )}

                {template.governing_law && (
                  <p className="text-xs text-slate-400 mb-2">Governing Law: {template.governing_law}</p>
                )}

                {template.file_name && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Upload size={12} /><span className="truncate">{template.file_name}</span>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>Version {template.version}</span>
                  {canManage && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedTemplate(template); setPreviewDialogOpen(true); }} data-testid={`preview-template-${template.id}`}>
                        <Eye size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleClone(template)} data-testid={`clone-template-${template.id}`} title="Clone template">
                        <Copy size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(template)} data-testid={`edit-template-${template.id}`}>
                        <Edit size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => { setSelectedTemplate(template); setDeleteDialogOpen(true); }} data-testid={`delete-template-${template.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <FileText size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 mb-4">No templates found</p>
          {canManage && (
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2 rounded-sm"><Plus size={18} />Create Template</Button>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate ? "Edit Template" : `Create ${templateTypeTab === "addendum" ? "Addendum" : "Contract"} Template`}</DialogTitle>
            <DialogDescription>
              {templateTypeTab === "addendum"
                ? "Build an addendum template with sections aligned to agreement amendment standards"
                : "Build a contract template with structured schedules aligned to professional standards"}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={createMode} onValueChange={setCreateMode} className="mt-4">
            <TabsList className="mb-4">
              <TabsTrigger value="builder" className="gap-2"><FileText size={16} />Section Builder</TabsTrigger>
              <TabsTrigger value="upload" className="gap-2"><Upload size={16} />Upload File</TabsTrigger>
            </TabsList>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Professional Services Agreement" className="rounded-sm" data-testid="template-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="template-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {activeCategories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Governing Law</Label>
                <Select value={formData.governing_law} onValueChange={(value) => setFormData({ ...formData, governing_law: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="governing-law-select"><SelectValue placeholder="Select jurisdiction" /></SelectTrigger>
                  <SelectContent>
                    {governingLawOptions.map((law) => (<SelectItem key={law} value={law}>{law}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3 space-y-2">
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description of this template" className="rounded-sm" data-testid="template-description-input" />
              </div>
            </div>

            {/* Template Metadata */}
            <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-sm space-y-3">
              <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500">Template Defaults</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Company Name</Label>
                  <Input value={formData.template_metadata?.company_name || ""} onChange={(e) => setFormData({ ...formData, template_metadata: { ...formData.template_metadata, company_name: e.target.value } })} placeholder="e.g., ProcureAide Ltd" className="rounded-sm h-9" data-testid="company-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Company Address</Label>
                  <Input value={formData.template_metadata?.company_address || ""} onChange={(e) => setFormData({ ...formData, template_metadata: { ...formData.template_metadata, company_address: e.target.value } })} placeholder="Office address" className="rounded-sm h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Termination Notice (Days)</Label>
                  <Input type="number" value={formData.template_metadata?.termination_notice_days || "30"} onChange={(e) => setFormData({ ...formData, template_metadata: { ...formData.template_metadata, termination_notice_days: e.target.value } })} className="rounded-sm h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Currency</Label>
                  <Select value={formData.template_metadata?.currency || "GBP"} onValueChange={(value) => setFormData({ ...formData, template_metadata: { ...formData.template_metadata, currency: value } })}>
                    <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="INR">INR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section Builder Tab */}
            <TabsContent value="builder" className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-medium">Template Sections / Schedules</h3>
                  <p className="text-sm text-slate-500">Add schedules and sections matching your contract structure</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {templateTypeTab === "contract" ? (
                    <Button onClick={handleLoadProfessionalServicesPreset} variant="outline" className="gap-2 rounded-sm text-sm" data-testid="load-preset-btn">
                      <Copy size={14} />
                      Load Professional Services Preset
                    </Button>
                  ) : (
                    <>
                      <Button onClick={handleLoadAddendumPreset} variant="outline" className="gap-2 rounded-sm text-sm" data-testid="load-addendum-preset-btn">
                        <Copy size={14} />
                        Load Amendment Preset
                      </Button>
                      <Button onClick={handleLoadExtensionPreset} variant="outline" className="gap-2 rounded-sm text-sm" data-testid="load-extension-preset-btn">
                        <Copy size={14} />
                        Load Extension Preset
                      </Button>
                    </>
                  )}
                  <Button onClick={handleAddSection} variant="outline" className="gap-2 rounded-sm" data-testid="add-section-btn">
                    <Plus size={16} />
                    Add Section
                  </Button>
                </div>
              </div>

              {formData.sections.length === 0 ? (
                <div className="border-2 border-dashed rounded-sm p-8 text-center">
                  <FileText size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 mb-4">No sections added yet. Start from a preset or add individual sections:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {activeSectionTypes.slice(0, 6).map((type) => (
                      <Button key={type.value} variant="outline" size="sm" className="rounded-sm" onClick={() => handleAddQuickSection(type.value)}>
                        + {type.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.sections.map((section, index) => (
                    <SectionCard
                      key={section.id}
                      section={section}
                      index={index}
                      total={formData.sections.length}
                      onUpdate={handleUpdateSection}
                      onRemove={handleRemoveSection}
                      onMove={handleMoveSection}
                      activeSectionTypes={activeSectionTypes}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="space-y-4">
              <div className="border-2 border-dashed rounded-sm p-8 text-center">
                {formData.file_name ? (
                  <div className="space-y-3">
                    <FileText size={40} className="mx-auto text-cyan-500" />
                    <p className="font-medium">{formData.file_name}</p>
                    <p className="text-sm text-slate-500">File uploaded successfully</p>
                    <Button variant="outline" size="sm" onClick={() => setFormData((prev) => ({ ...prev, file_url: null, file_name: null }))} className="rounded-sm">Remove File</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload size={40} className="mx-auto text-slate-300" />
                    <div>
                      <p className="font-medium">Upload a template file</p>
                      <p className="text-sm text-slate-500">Supported: PDF, Word (.doc, .docx), Text files</p>
                    </div>
                    <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleFileUpload} className="hidden" />
                    <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="gap-2 rounded-sm" data-testid="upload-file-btn">
                      {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      {uploading ? "Uploading..." : "Select File"}
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Additional Content (Optional)</Label>
                <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} placeholder="Add any additional template content or notes..." rows={6} className="rounded-sm" data-testid="template-content-input" />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-template-btn">
              {saving && <Loader2 size={16} className="animate-spin mr-2" />}
              {selectedTemplate ? "Update Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.category} {selectedTemplate?.governing_law ? `| ${selectedTemplate.governing_law}` : ""} | Version {selectedTemplate?.version}
            </DialogDescription>
          </DialogHeader>

          {selectedTemplate?.sections?.length > 0 ? (
            <div className="space-y-4 mt-4">
              {selectedTemplate.sections.map((section, index) => (
                <div key={section.name || `section-${index}`} className={`border rounded-sm p-4 ${!section.is_editable ? "bg-amber-50/50 dark:bg-amber-950/10 border-amber-200 dark:border-amber-800" : ""}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">{section.name}</h3>
                    <div className="flex items-center gap-2">
                      <Badge variant={section.is_editable ? "outline" : "secondary"}>
                        {section.is_editable ? "Editable" : "Locked"}
                      </Badge>
                      {!section.is_editable && section.allow_override && (
                        <Badge variant="outline" className="text-blue-600 border-blue-300">Override Allowed</Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">{section.content || "No content"}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4">
              <pre className="text-sm whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-4 rounded-sm">{selectedTemplate?.content || "No content"}</pre>
            </div>
          )}

          {selectedTemplate?.file_name && (
            <div className="flex items-center gap-2 mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-sm">
              <FileText size={16} className="text-slate-500" />
              <span className="text-sm">{selectedTemplate.file_name}</span>
              <Button variant="ghost" size="sm" className="ml-auto gap-1" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/templates/files/${selectedTemplate.file_url}`, "_blank")}>
                <Download size={14} />Download
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{selectedTemplate?.name}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-delete-template-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
